import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Warning,
  Lock,
  Archive,
  Visibility
} from '@mui/icons-material';
import fileStatusService from '../../services/fileStatusService';
import { useAuth } from '../../contexts/Authcontexts';
import { useSnackbar } from 'notistack';

const FileStatusWorkflow = ({ file, onStatusUpdate }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [fileActions, setFileActions] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [loading, setLoading] = useState(false);

  const statusOrder = ['OPEN', 'IN_PROGRESS', 'DEFICIENT', 'COMPLETED', 'CLOSED', 'ARCHIVED'];
  
  const getStatusIcon = (status, isActive, isCompleted) => {
    if (isCompleted) return <CheckCircle color="success" />;
    if (isActive) {
      switch (status) {
        case 'DEFICIENT': return <Warning color="error" />;
        case 'CLOSED': return <Lock color="secondary" />;
        case 'ARCHIVED': return <Archive color="default" />;
        default: return <RadioButtonUnchecked color="primary" />;
      }
    }
    return <RadioButtonUnchecked color="disabled" />;
  };

  const getCurrentStepIndex = () => {
    return statusOrder.indexOf(file.fileStatus);
  };

  useEffect(() => {
    if (file?._id) {
      loadFileActions();
      loadStatusHistory();
    }
  }, [file]);

  const loadFileActions = async () => {
    try {
      const response = await fileStatusService.getFileActions(file._id);
      setFileActions(response.data);
    } catch (error) {
      console.error('Error loading file actions:', error);
    }
  };

  const loadStatusHistory = async () => {
    try {
      const response = await fileStatusService.getFileStatusHistory(file._id);
      setStatusHistory(response.data.statusHistory || []);
    } catch (error) {
      console.error('Error loading status history:', error);
    }
  };

  const handleStatusChange = async () => {
    if (!selectedStatus || !statusReason.trim()) {
      enqueueSnackbar('Please select status and provide reason', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      await fileStatusService.updateFileStatus(file._id, selectedStatus, statusReason);
      enqueueSnackbar('File status updated successfully', { variant: 'success' });
      setOpenStatusDialog(false);
      setSelectedStatus('');
      setStatusReason('');
      
      if (onStatusUpdate) {
        onStatusUpdate();
      }
      
      // Reload data
      loadFileActions();
      loadStatusHistory();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Error updating status', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!file) return null;

  const currentStepIndex = getCurrentStepIndex();
  const allowedTransitions = fileActions?.allowedTransitions || [];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        File Status Workflow
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Current Status: 
          <Chip 
            label={fileStatusService.getStatusDisplayName(file.fileStatus)}
            color={fileStatusService.getStatusColor(file.fileStatus)}
            size="small"
            sx={{ ml: 1 }}
          />
        </Typography>
        
        {fileActions && (
          <Typography variant="body2" color="text.secondary">
            Available Actions: {fileActions.allowedActions.join(', ')}
          </Typography>
        )}
      </Box>

      <Stepper activeStep={currentStepIndex} orientation="vertical">
        {statusOrder.map((status, index) => {
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          const statusName = fileStatusService.getStatusDisplayName(status);
          
          return (
            <Step key={status} completed={isCompleted}>
              <StepLabel
                icon={getStatusIcon(status, isActive, isCompleted)}
                optional={
                  isActive && (
                    <Typography variant="caption">Current Status</Typography>
                  )
                }
              >
                {statusName}
              </StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getStatusDescription(status)}
                  </Typography>
                  
                  {isActive && allowedTransitions.length > 0 && user?.role === 'ADMIN' && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setOpenStatusDialog(true)}
                      >
                        Change Status
                      </Button>
                    </Box>
                  )}
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>

      {statusHistory.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Status History
          </Typography>
          {statusHistory.map((history, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{fileStatusService.getStatusDisplayName(history.status)}</strong>
                {' - '}
                {new Date(history.timestamp).toLocaleString()}
                {' by '}
                {history.user}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Status Change Dialog */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change File Status</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will change the file status. Please ensure this is necessary.
          </Alert>
          
          <TextField
            select
            fullWidth
            label="New Status"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mb: 2 }}
          >
            <option value="">Select Status</option>
            {allowedTransitions.map((status) => (
              <option key={status} value={status}>
                {fileStatusService.getStatusDisplayName(status)}
              </option>
            ))}
          </TextField>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Status Change"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Please provide a reason for this status change..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleStatusChange} 
            variant="contained" 
            disabled={loading || !selectedStatus || !statusReason.trim()}
          >
            {loading ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const getStatusDescription = (status) => {
  const descriptions = {
    'OPEN': 'File has been created and is ready for processing',
    'IN_PROGRESS': 'File is being actively worked on',
    'DEFICIENT': 'File has deficiencies that need to be resolved',
    'COMPLETED': 'All work on the file has been completed',
    'CLOSED': 'File has been officially closed',
    'ARCHIVED': 'File has been archived for long-term storage'
  };
  return descriptions[status] || '';
};

export default FileStatusWorkflow;